import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import GpsFixed from "@mui/icons-material/GpsFixed";

import MDBox from "components/MDBox";
import MDBadgeDot from "components/MDBadgeDot";
import MDTypography from "components/MDTypography";
import React, { useState, useEffect, useRef, useMemo } from "react";
import "react-circular-progressbar/dist/styles.css";
import PropTypes from "prop-types";
import { Typography, Box, Stack, Popover } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import {
  getPointsTextColor,
  removeYearFromDateFormat,
  numberFormatter,
} from "utils/HelperFunctions";
import { getAPICall } from "lib/callApi";
import { GET_STREAK_CALENDAR_DATA } from "lib/constants";
import { useTheme } from "@emotion/react";
import TargetPercentage from "components/TargetPercentage";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ConfettiExplosion from "react-confetti-explosion";
import TooltipLight from "utils/TooltipLight";
import FlameIcon from "components/Icons/FlameIcon";
import { homeAction } from "redux/slice/home";
import AchievementCalendar from "./AchievementCalendar";
import SoundToggle from "./SoundToggle";
import "styles/achievements.scss";

function CircularProgressChart({
  kpiEntries,
  lastUpdated,
  targetPoints,
  activeStreak,
  pointGoals = [],
  workingDays = "0111110",
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isExploding, setIsExploding] = useState(false);
  const totalAchievedPoints = parseInt(
    kpiEntries?.reduce((acc, obj) => acc + parseInt(obj?.DailyActions?.[0]?.points, 10), 0),
    10
  );
  const user = useSelector((state) => state.loginUser.loginUserState);
  const { latestCelebrationDate, soundEnabled } = useSelector((state) => state.home.celebration);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [streakCalendarData, setStreakCalendarData] = useState([]);
  const explosionDuration = 5000;
  const MINIMUM_STREAK_DAYS = 3;
  const fireworkRef = useRef(null);
  const chimeRef = useRef(null);

  useEffect(() => {
    const prefix = process.env.REACT_APP_CDN_ASSET_PREFIX || "/assets";
    fireworkRef.current = new Audio(`${prefix}/audio/firework.mp3`);
    fireworkRef.current.load();
    chimeRef.current = new Audio(`${prefix}/audio/chime2.mp3`);
    chimeRef.current.load();
    dispatch(homeAction.getCelebrationDetailsAction());
  }, []);

  const playSound = () => {
    if (fireworkRef.current && chimeRef.current) {
      fireworkRef.current.currentTime = 0;
      chimeRef.current.currentTime = 0;
      fireworkRef.current.play();
      chimeRef.current.play();
    }
  };

  const handleStreakClick = async (event) => {
    const anchorEl = event.currentTarget;
    const result = await getAPICall(GET_STREAK_CALENDAR_DATA, true);
    if (result?.data?.success) {
      setStreakCalendarData(result.data.data);
      setCalendarAnchor(anchorEl);
    }
  };

  const handleClose = () => {
    setCalendarAnchor(null);
  };

  const explosionProps = {
    force: 0.6,
    duration: explosionDuration,
    particleCount: 200,
    floorHeight: 2400,
    floorWidth: 1600,
    colors: [
      theme.palette.primaryRed.main,
      theme.palette.secondaryBlue.main,
      theme.palette.secondaryGreen.main,
    ],
  };

  const labels = [];
  const data = [];
  const graphKpiColors = [];

  kpiEntries.forEach((entry) => {
    if (Number(entry?.DailyActions?.[0]?.points)) {
      labels.push(entry?.KpiActivity?.name);
      data.push(Number(entry.DailyActions?.[0]?.points));
      graphKpiColors.push(generateColorFromString(entry?.KpiActivity?.name));
    }
  });

  const percentage = (totalAchievedPoints / 10) * 100;
  const prevPercentageRef = useRef(null);
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (prevPercentageRef.current !== null) {
      const day = moment().format("YYYY-MM-DD");
      if (prevPercentageRef.current < 100 && percentage >= 100) {
        if (day !== latestCelebrationDate) {
          if (!isExploding) {
            setIsExploding(true);
            if (soundEnabled) {
              playSound();
            }
          }
          dispatch(homeAction.updateCelebrationDate({ celebrationDate: day }));
        }
      }
    }

    prevPercentageRef.current = percentage;
  }, [percentage]);

  useEffect(() => {
    let timer;
    if (isExploding) {
      timer = setTimeout(() => {
        setIsExploding(false);
      }, explosionDuration);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isExploding]);

  const confettiExplosion = useMemo(() => {
    if (isExploding) {
      return <ConfettiExplosion {...explosionProps} />;
    }
    return null;
  }, [isExploding]);

  const renderStreakTooltip = () => (
    <Stack direction="row" alignItems="center">
      <FlameIcon />
      <Typography
        sx={{ fontSize: "0.8rem" }}
      >{`Hit your point goal ${MINIMUM_STREAK_DAYS} straight days to start streaking!`}</Typography>
    </Stack>
  );

  const getStreakNotification = () => {
    let message = "";
    const currentTime = moment.tz(user.timezone);
    const timeCondition = currentTime.hour() >= 15;
    const pointCondition = totalAchievedPoints < targetPoints;
    const workingDayCondition = user?.workingDays?.[currentTime.weekday()] === "1";

    if (timeCondition && pointCondition && workingDayCondition && activeStreak >= 3) {
      message = `Hit ${targetPoints} points to keep the streak going!`;
    } else if (timeCondition && pointCondition && workingDayCondition && activeStreak === 2) {
      message = `You're so close! Hit ${targetPoints} points to start streaking!`;
    }
    return message;
  };

  return (
    <Card sx={{ height: "320px", p: 2 }}>
      <MDBox
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          "& .big-text": { fontSize: "1rem", fontWeight: "bold" },
        }}
      >
        <Stack>
          <Typography variant="h6" color="info" mb={0} display="block" ml="0.5rem">
            {moment.tz(user.timezone).format("dddd MMM Do")}{" "}
          </Typography>
          <SoundToggle user={user} soundEnabled={soundEnabled} />
        </Stack>
        {lastUpdated && (
          <TooltipLight
            title={`Last updated ${moment
              .tz(lastUpdated, user.timezone)
              .format(removeYearFromDateFormat(user.dateFormat))} at ${moment
              .tz(lastUpdated, user.timezone)
              .format("hh:mm a")}`}
          >
            <AccessTimeIcon
              sx={{
                cursor: "pointer",
              }}
            />
          </TooltipLight>
        )}
      </MDBox>
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          height: "100%",
          overflowWrap: "anywhere",
          "& .CircularProgressbar-text": { fontSize: "12px" },
          maxWidth: "400px",
          margin: "auto",
          px: 2,
        }}
      >
        <Grid container alignItems="center">
          <Grid
            item
            xs={5}
            sx={{
              "& .subtext": { fontSize: "12px", paddingBottom: ".6rem" },
              position: "relative",
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                left: "1.25em",
                height: "3.3em",
                mt: "1.7em",
                borderLeft: "1px dashed #aaa",
              }}
            />
            <MDBox pl={2} sx={{ display: "inline-flex" }}>
              <MDBadgeDot
                color="warning"
                size="lg"
                sx={{
                  p: 0.5,
                  i: {
                    background: getPointsTextColor(totalAchievedPoints, 10),
                  },
                }}
                badgeContent=""
              />
              <MDBox
                pl={1}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <MDTypography
                  variant="h4"
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    width: "100%",
                    // color: getPointsTextColor(totalAchievedPoints, 10),
                  }}
                >
                  {numberFormatter(totalAchievedPoints)}
                  <MDBox
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                  >
                    <MDTypography
                      variant="subtitle1"
                      sx={{
                        fontSize: "60%",
                        // color: getPointsTextColor(totalAchievedPoints, 10),
                      }}
                    >
                      {parseInt(totalAchievedPoints, 10) > 1 ? "pts" : "pt"}
                    </MDTypography>
                  </MDBox>
                </MDTypography>
                <MDTypography variant="caption" color="gray40">
                  achieved
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox
              pl={2}
              sx={{
                pt: 5,
                display: "inline-flex",
              }}
            >
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: "-1px",
                  mr: "4px",
                }}
              >
                <GpsFixed
                  sx={{
                    p: 0.2,
                  }}
                />
              </MDBox>
              <MDBox
                pl={1}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <MDTypography
                  variant="h4"
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    width: "100%",
                  }}
                >
                  {numberFormatter(targetPoints)}
                  <MDBox
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      mb: 0.1,
                    }}
                  >
                    <MDTypography
                      variant="subtitle1"
                      sx={{
                        fontSize: "60%",
                      }}
                    >
                      {parseInt(targetPoints, 10) > 1 ? "pts" : "pt"}
                    </MDTypography>
                  </MDBox>
                </MDTypography>
                <MDTypography variant="caption" color="gray40">
                  daily target
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={7} p={1} sx={{ position: "relative" }}>
            {isExploding && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  zIndex: 1,
                }}
              >
                {confettiExplosion}
              </Box>
            )}
            <TargetPercentage percentage={percentage} width="100%" height="100%" strokeWidth="9" />
          </Grid>
        </Grid>
      </MDBox>

      <Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          width="100%"
          alignItems="center"
          spacing={2}
          textAlign="right"
        >
          <Typography
            sx={{
              fontSize: "1rem",
              lineHeight: "1rem",
              pt: "0.4rem",
              fontWeight: 700,
              color: "#ff9c3b",
            }}
          >
            {getStreakNotification()}
          </Typography>
          <Stack
            direction="row"
            onClick={handleStreakClick}
            sx={{ "& :hover": { cursor: "pointer" } }}
          >
            <FlameIcon variant={activeStreak < MINIMUM_STREAK_DAYS ? "gray" : ""} />
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: activeStreak < MINIMUM_STREAK_DAYS ? "#9292a8" : "#ff8a1a",
                pt: "0.4rem",
                ml: "0.25rem",
              }}
            >
              {activeStreak}
            </Typography>
          </Stack>
        </Stack>

        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiPopover-paper": {
              p: 0,
              boxShadow:
                "0px 5px 5px -3px rgba(0, 0, 0, 0.1), 0px 8px 10px 1px rgba(0, 0, 0, 0.08), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <Card>
            <AchievementCalendar
              pointGoals={pointGoals}
              workingDays={workingDays}
              streakCalendarData={streakCalendarData}
            />
            <TooltipLight title={renderStreakTooltip()} placement="top">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                py={1}
                sx={{ "& :hover": { cursor: "default" } }}
              >
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "500",
                    pt: "0.4rem",
                    mr: "0.25rem",
                  }}
                >
                  Current Streak:
                </Typography>
                <FlameIcon variant={activeStreak < MINIMUM_STREAK_DAYS ? "gray" : ""} />
                <Typography
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: activeStreak < MINIMUM_STREAK_DAYS ? "#9292a8" : "#ff8a1a",
                    pt: "0.4rem",
                    ml: "0.25rem",
                  }}
                >
                  {`${activeStreak} ${activeStreak === 1 ? "DAY" : "DAYS"}`}
                </Typography>
                <HelpOutlineOutlinedIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    mt: "0.25rem",
                    ml: "0.4rem",
                    color: "#9292a8",
                  }}
                />
              </Stack>
            </TooltipLight>
          </Card>
        </Popover>
      </Box>
    </Card>
  );
}

export default CircularProgressChart;

CircularProgressChart.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  kpiEntries: PropTypes.array.isRequired,
};
